import { PageHeader } from '@/shared/components/ui/PageHeader';
import { useState } from 'react';
import { toast } from 'react-hot-toast';
import { Cable, Eye, FileText, Mail, MessageCircle, Pencil, Plus, QrCode, Radio, ScrollText, Send, Users } from 'lucide-react';
import { Modal } from '@/shared/components/ui/Modal';
import { Badge } from '@/shared/components/ui/Badge';
import { Button } from '@/shared/components/ui/Button';
import { Card, CardBody, CardHeader } from '@/shared/components/ui/Card';
import { EmptyState } from '@/shared/components/ui/EmptyState';
import { Input } from '@/shared/components/ui/Input';
import { Select } from '@/shared/components/ui/Select';
import { Spinner } from '@/shared/components/ui/Spinner';
import { useCommLogs, useCommMutations, useCommSettings } from '../api';
import { useEmailTemplates, useUpdateEmailTemplate, useToggleEmailTemplate, usePreviewEmailTemplate } from '../api';
import { WhatsappFullClient } from '../components/WhatsappFullClient';
import { TeamMailboxesTab } from '@/features/mailbox/components/TeamMailboxesTab';

const textareaClass =
  'w-full rounded border border-outline bg-surface px-3 py-2 text-data-md text-on-surface focus:outline-none focus:ring-2 focus:ring-primary/40';

function errorMessage(err: unknown, fallback: string): string {
  if (err && typeof err === 'object') {
    const maybe = err as { response?: { data?: { error?: string } } };
    if (maybe.response?.data?.error) return maybe.response.data.error;
  }
  return fallback;
}

function StatusBadge({ ok, okLabel, notLabel }: { ok: boolean; okLabel: string; notLabel: string }) {
  return <Badge tone={ok ? 'success' : 'neutral'}>{ok ? okLabel : notLabel}</Badge>;
}

type Tab = 'email' | 'mailboxes' | 'whatsapp' | 'whatsapp-web' | 'connectors' | 'logs' | 'templates';

export function CommunicationHubPage() {
  const [tab, setTab] = useState<Tab>('email');
  const { data: settings, isLoading } = useCommSettings();

  return (
    <div className="flex h-full flex-col gap-4 overflow-y-auto p-4 md:p-6">
      <PageHeader
        icon={Radio}
        eyebrow="Administration"
        title="Communication Hub"
        description={<>Email engine, WhatsApp (Business API and Web), and third-party connectors</>}
      />

      {isLoading || !settings ? (
        <div className="flex flex-1 items-center justify-center">
          <Spinner />
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 gap-3 md:grid-cols-3" aria-label="Connection status">
            <Card>
              <CardBody className="flex items-center justify-between p-4">
                <span className="flex items-center gap-2 text-body-sm font-medium text-on-surface">
                  <Mail className="h-4 w-4" aria-hidden /> Email (SMTP)
                </span>
                <StatusBadge ok={settings.email.configured} okLabel="Configured" notLabel="Not configured" />
              </CardBody>
            </Card>
            <Card>
              <CardBody className="flex items-center justify-between p-4">
                <span className="flex items-center gap-2 text-body-sm font-medium text-on-surface">
                  <MessageCircle className="h-4 w-4" aria-hidden /> WhatsApp Business
                </span>
                <StatusBadge ok={settings.whatsapp.configured} okLabel="Connected" notLabel="Not connected" />
              </CardBody>
            </Card>
            <Card>
              <CardBody className="flex items-center justify-between p-4">
                <span className="flex items-center gap-2 text-body-sm font-medium text-on-surface">
                  <Cable className="h-4 w-4" aria-hidden /> Payment gateway
                </span>
                <StatusBadge ok={settings.connectors.paymentConfigured} okLabel="Configured" notLabel="Not configured" />
              </CardBody>
            </Card>
          </div>

          <div role="tablist" aria-label="Communication Hub sections" className="flex gap-1 border-b border-outline">
            {(
              [
                { key: 'email', label: 'Email', icon: Mail },
                { key: 'mailboxes', label: 'Team Mailboxes', icon: Users },
                { key: 'whatsapp', label: 'WhatsApp Business API', icon: MessageCircle },
                { key: 'whatsapp-web', label: 'WhatsApp Web (QR)', icon: QrCode },
                { key: 'connectors', label: 'Connectors', icon: Cable },
                { key: 'logs', label: 'Message Log', icon: ScrollText },
                { key: 'templates', label: 'Email Templates', icon: FileText },
              ] as const
            ).map((t) => (
              <button
                key={t.key}
                role="tab"
                aria-selected={tab === t.key}
                onClick={() => setTab(t.key)}
                className={`flex items-center gap-2 rounded-t-md px-4 py-2 text-body-sm font-medium transition-colors ${
                  tab === t.key ? 'border-b-2 border-primary text-primary' : 'text-on-surface-variant hover:text-on-surface'
                }`}
              >
                <t.icon className="h-4 w-4" aria-hidden />
                {t.label}
              </button>
            ))}
          </div>

          {tab === 'email' && <EmailTab settings={settings} />}
          {tab === 'mailboxes' && <TeamMailboxesTab />}
          {tab === 'whatsapp' && <WhatsappTab settings={settings} />}
          {tab === 'whatsapp-web' && <WhatsappFullClient canManage={settings.canManage} />}
          {tab === 'connectors' && <ConnectorsTab settings={settings} />}
          {tab === 'logs' && <LogsTab />}
          {tab === 'templates' && <TemplatesTab />}
        </>
      )}
    </div>
  );
}

type SettingsView = NonNullable<ReturnType<typeof useCommSettings>['data']>;

/* ── Email tab ───────────────────────────────────────────────────────────── */

function EmailTab({ settings }: { settings: SettingsView }) {
  const { saveEmailSettings, sendEmail } = useCommMutations();
  const [host, setHost] = useState(settings.email.host);
  const [port, setPort] = useState(String(settings.email.port));
  const [secure, setSecure] = useState(settings.email.secure);
  const [user, setUser] = useState('');
  const [pass, setPass] = useState('');
  const [fromName, setFromName] = useState(settings.email.fromName);
  const [fromEmail, setFromEmail] = useState(settings.email.fromEmail);

  const [to, setTo] = useState('');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');

  return (
    <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
      {settings.canManage && (
        <Card>
          <CardHeader title="SMTP settings" subtitle="Password is write-only — leave blank to keep the saved one" />
          <CardBody className="flex flex-col gap-3">
            {!settings.email.dependencyInstalled && (
              <p className="rounded-md bg-warning/10 p-2 text-body-sm text-on-surface">
                The <code>nodemailer</code> dependency is not installed yet — run <code>npm install</code> in the
                backend folder once, then restart the server.
              </p>
            )}
            <div className="grid grid-cols-2 gap-3">
              <Input label="SMTP host" name="smtp-host" value={host} onChange={(e) => setHost(e.target.value)} placeholder="smtp.gmail.com" />
              <Input label="Port" name="smtp-port" type="number" value={port} onChange={(e) => setPort(e.target.value)} />
            </div>
            <label className="flex items-center gap-2 text-body-sm text-on-surface">
              <input type="checkbox" checked={secure} onChange={(e) => setSecure(e.target.checked)} />
              Use TLS from the start (port 465). Leave off for STARTTLS (587).
            </label>
            <div className="grid grid-cols-2 gap-3">
              <Input
                label={`Username${settings.email.user ? ` (saved: ${settings.email.user})` : ''}`}
                name="smtp-user"
                value={user}
                onChange={(e) => setUser(e.target.value)}
              />
              <Input label="Password / app password" name="smtp-pass" type="password" value={pass} onChange={(e) => setPass(e.target.value)} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Input label="From name" name="smtp-from-name" value={fromName} onChange={(e) => setFromName(e.target.value)} />
              <Input label="From email" name="smtp-from-email" type="email" value={fromEmail} onChange={(e) => setFromEmail(e.target.value)} />
            </div>
            <Button
              disabled={saveEmailSettings.isPending}
              onClick={() =>
                saveEmailSettings.mutate(
                  { host, port: Number(port) || 587, secure, user, pass, fromName, fromEmail },
                  {
                    onSuccess: () => toast.success('Email settings saved.'),
                    onError: (err) => toast.error(errorMessage(err, 'Failed to save settings.')),
                  },
                )
              }
            >
              Save SMTP settings
            </Button>
          </CardBody>
        </Card>
      )}

      <Card>
        <CardHeader title="Send email" subtitle={settings.email.configured ? `Sends as ${settings.email.fromEmail}` : 'Configure SMTP first'} />
        <CardBody className="flex flex-col gap-3">
          <Input label="To" name="email-to" type="email" value={to} onChange={(e) => setTo(e.target.value)} />
          <Input label="Subject" name="email-subject" value={subject} onChange={(e) => setSubject(e.target.value)} />
          <div>
            <label htmlFor="email-body" className="mb-1.5 block text-body-sm font-medium text-on-surface">
              Message
            </label>
            <textarea id="email-body" rows={5} className={textareaClass} value={body} onChange={(e) => setBody(e.target.value)} />
          </div>
          <Button
            disabled={!settings.email.configured || sendEmail.isPending}
            onClick={() =>
              sendEmail.mutate(
                { to: to.trim(), subject: subject.trim(), body },
                {
                  onSuccess: () => {
                    toast.success('Email sent.');
                    setSubject('');
                    setBody('');
                  },
                  onError: (err) => toast.error(errorMessage(err, 'Email failed.')),
                },
              )
            }
          >
            <Send className="h-4 w-4" aria-hidden />
            {sendEmail.isPending ? 'Sending…' : 'Send'}
          </Button>
          {!settings.email.configured && (
            <p className="text-body-sm text-on-surface-variant">
              Sending is disabled until SMTP credentials are saved — no messages are simulated.
            </p>
          )}
        </CardBody>
      </Card>
    </div>
  );
}

/* ── WhatsApp tab ────────────────────────────────────────────────────────── */

function WhatsappTab({ settings }: { settings: SettingsView }) {
  const { saveWhatsappSettings, sendWhatsapp } = useCommMutations();
  const [provider, setProvider] = useState<'meta' | 'interakt'>(settings.whatsapp.provider || 'meta');
  const [phoneNumberId, setPhoneNumberId] = useState('');
  const [accessToken, setAccessToken] = useState('');
  const [businessAccountId, setBusinessAccountId] = useState('');
  const [verifyToken, setVerifyToken] = useState('');
  const [apiKey, setApiKey] = useState('');

  const [to, setTo] = useState('');
  const [message, setMessage] = useState('');

  return (
    <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
      {settings.canManage && (
        <Card>
          <CardHeader
            title="WhatsApp Business API"
            subtitle={provider === 'interakt'
              ? 'From Interakt → Settings → Developer Settings. API Key is write-only.'
              : 'From Meta Business → WhatsApp → API Setup. Access token is write-only.'}
          />
          <CardBody className="flex flex-col gap-3">
            {settings.whatsapp.source === 'integrations' && (
              <div className="rounded-lg border border-success/30 bg-success/10 px-3 py-2 text-body-sm text-success-text">
                Connected through <b className="font-semibold">Integrations → Interakt</b> — WhatsApp sends with that key. Nothing needs filling in here.
              </div>
            )}
            <div>
              <label htmlFor="wa-provider" className="mb-1.5 block text-body-sm font-medium text-on-surface">
                Provider
              </label>
              <select
                id="wa-provider"
                className={textareaClass}
                value={provider}
                onChange={(e) => setProvider(e.target.value as 'meta' | 'interakt')}
              >
                <option value="meta">Meta (direct WhatsApp Cloud API)</option>
                <option value="interakt">Interakt</option>
              </select>
            </div>

            {provider === 'interakt' ? (
              <Input
                label={`Interakt API Key${settings.whatsapp.apiKeySet ? ' (already set)' : ''}`}
                name="wa-interakt-key"
                type="password"
                value={apiKey}
                onChange={(e) => setApiKey(e.target.value)}
              />
            ) : (
              <>
                <Input
                  label={`Phone Number ID${settings.whatsapp.phoneNumberId ? ` (saved: ${settings.whatsapp.phoneNumberId})` : ''}`}
                  name="wa-phone-id"
                  value={phoneNumberId}
                  onChange={(e) => setPhoneNumberId(e.target.value)}
                />
                <Input label="Permanent access token" name="wa-token" type="password" value={accessToken} onChange={(e) => setAccessToken(e.target.value)} />
                <Input
                  label="Business Account ID (optional)"
                  name="wa-business-id"
                  value={businessAccountId}
                  onChange={(e) => setBusinessAccountId(e.target.value)}
                />
              </>
            )}

            <Input
              label={`Webhook verify token${settings.whatsapp.webhookVerifyTokenSet ? ' (already set)' : ''}${provider === 'interakt' ? ' (Meta only — leave blank)' : ''}`}
              name="wa-verify-token"
              value={verifyToken}
              onChange={(e) => setVerifyToken(e.target.value)}
              disabled={provider === 'interakt'}
            />
            <p className="text-body-sm text-on-surface-variant">
              {provider === 'interakt' ? (
                <>Inbound webhook URL for Interakt (paste into Interakt → Settings → Webhooks): <code>{'{your-server}'}/api/comm/whatsapp/webhook/interakt</code></>
              ) : (
                <>Webhook URL for Meta: <code>{'{your-server}'}/api/comm/whatsapp/webhook</code></>
              )}
            </p>
            <Button
              disabled={saveWhatsappSettings.isPending}
              onClick={() =>
                saveWhatsappSettings.mutate(
                  { provider, phoneNumberId, accessToken, businessAccountId, verifyToken, apiKey },
                  {
                    onSuccess: () => toast.success('WhatsApp settings saved.'),
                    onError: (err) => toast.error(errorMessage(err, 'Failed to save settings.')),
                  },
                )
              }
            >
              Save WhatsApp settings
            </Button>
          </CardBody>
        </Card>
      )}

      <Card>
        <CardHeader title="Send WhatsApp message" subtitle={settings.whatsapp.configured ? `Via ${settings.whatsapp.provider === 'interakt' ? 'Interakt' : 'Meta Graph API'}` : 'Connect the Business API first'} />
        <CardBody className="flex flex-col gap-3">
          <Input
            label="To (with country code)"
            name="wa-to"
            value={to}
            onChange={(e) => setTo(e.target.value)}
            placeholder="+91XXXXXXXXXX"
          />
          <div>
            <label htmlFor="wa-message" className="mb-1.5 block text-body-sm font-medium text-on-surface">
              Message
            </label>
            <textarea id="wa-message" rows={4} className={textareaClass} value={message} onChange={(e) => setMessage(e.target.value)} />
          </div>
          <Button
            disabled={!settings.whatsapp.configured || sendWhatsapp.isPending}
            onClick={() =>
              sendWhatsapp.mutate(
                { to: to.trim(), message },
                {
                  onSuccess: () => {
                    toast.success('WhatsApp message sent.');
                    setMessage('');
                  },
                  onError: (err) => toast.error(errorMessage(err, 'WhatsApp send failed.')),
                },
              )
            }
          >
            <Send className="h-4 w-4" aria-hidden />
            {sendWhatsapp.isPending ? 'Sending…' : 'Send'}
          </Button>
          {!settings.whatsapp.configured && (
            <p className="text-body-sm text-on-surface-variant">
              Sending is disabled until the Business API credentials are saved — nothing is simulated.
            </p>
          )}
        </CardBody>
      </Card>
    </div>
  );
}

/* ── Connectors tab ──────────────────────────────────────────────────────── */

function ConnectorsTab({ settings }: { settings: SettingsView }) {
  const { saveConnectorSettings } = useCommMutations();
  const [paymentProvider, setPaymentProvider] = useState(settings.connectors.paymentProvider);
  const [paymentKeyId, setPaymentKeyId] = useState('');
  const [paymentKeySecret, setPaymentKeySecret] = useState('');
  const [calendarIcsUrl, setCalendarIcsUrl] = useState(settings.connectors.calendarIcsUrl);
  const [storageProvider, setStorageProvider] = useState(settings.connectors.storageProvider);
  const [storageBucket, setStorageBucket] = useState('');
  const [storageKeyId, setStorageKeyId] = useState('');
  const [storageKeySecret, setStorageKeySecret] = useState('');

  if (!settings.canManage) {
    return (
      <EmptyState
        title="Administrator access required"
        description="Connector credentials can only be managed by administrators."
      />
    );
  }

  return (
    <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
      <Card>
        <CardHeader title="Payment gateway" subtitle="Secrets are write-only" />
        <CardBody className="flex flex-col gap-3">
          <Select
            label="Provider"
            name="pay-provider"
            options={[
              { label: 'Razorpay', value: 'razorpay' },
              { label: 'Stripe', value: 'stripe' },
            ]}
            placeholder="Not configured"
            value={paymentProvider}
            onChange={(e) => setPaymentProvider(e.target.value)}
          />
          <Input
            label={`Key ID${settings.connectors.paymentKeyId ? ` (saved: ${settings.connectors.paymentKeyId})` : ''}`}
            name="pay-key-id"
            value={paymentKeyId}
            onChange={(e) => setPaymentKeyId(e.target.value)}
          />
          <Input label="Key secret" name="pay-key-secret" type="password" value={paymentKeySecret} onChange={(e) => setPaymentKeySecret(e.target.value)} />
        </CardBody>
      </Card>

      <Card>
        <CardHeader title="Calendar & storage" />
        <CardBody className="flex flex-col gap-3">
          <Input
            label="Calendar ICS feed URL"
            name="calendar-ics"
            value={calendarIcsUrl}
            onChange={(e) => setCalendarIcsUrl(e.target.value)}
            placeholder="https://calendar.google.com/…/basic.ics"
          />
          <Select
            label="Storage provider"
            name="storage-provider"
            options={[
              { label: 'Amazon S3 (compatible)', value: 's3' },
              { label: 'Google Drive', value: 'gdrive' },
            ]}
            placeholder="Not configured"
            value={storageProvider}
            onChange={(e) => setStorageProvider(e.target.value)}
          />
          <Input label="Bucket / folder" name="storage-bucket" value={storageBucket} onChange={(e) => setStorageBucket(e.target.value)} />
          <div className="grid grid-cols-2 gap-3">
            <Input label="Access key ID" name="storage-key-id" value={storageKeyId} onChange={(e) => setStorageKeyId(e.target.value)} />
            <Input label="Secret" name="storage-secret" type="password" value={storageKeySecret} onChange={(e) => setStorageKeySecret(e.target.value)} />
          </div>
        </CardBody>
      </Card>

      <div className="xl:col-span-2">
        <Button
          disabled={saveConnectorSettings.isPending}
          onClick={() =>
            saveConnectorSettings.mutate(
              {
                paymentProvider,
                paymentKeyId,
                paymentKeySecret,
                calendarIcsUrl,
                storageProvider,
                storageBucket,
                storageKeyId,
                storageKeySecret,
              },
              {
                onSuccess: () => toast.success('Connector settings saved.'),
                onError: (err) => toast.error(errorMessage(err, 'Failed to save settings.')),
              },
            )
          }
        >
          Save connector settings
        </Button>
        <p className="mt-2 text-body-sm text-on-surface-variant">
          Saved credentials are stored server-side and used by future payment/calendar/storage features. Status
          badges reflect whether keys are present — they are not a live connection test.
        </p>
      </div>
    </div>
  );
}

/* ── Logs tab ────────────────────────────────────────────────────────────── */

function LogsTab() {
  const [channel, setChannel] = useState<'' | 'email' | 'whatsapp'>('');
  const { data: logs = [], isLoading } = useCommLogs(channel);

  return (
    <div className="flex flex-col gap-3">
      <div className="w-48">
        <Select
          label="Channel"
          name="log-channel"
          options={[
            { label: 'Email', value: 'email' },
            { label: 'WhatsApp', value: 'whatsapp' },
          ]}
          placeholder="All channels"
          value={channel}
          onChange={(e) => setChannel(e.target.value as '' | 'email' | 'whatsapp')}
        />
      </div>
      {isLoading ? (
        <div className="flex h-24 items-center justify-center">
          <Spinner />
        </div>
      ) : logs.length === 0 ? (
        <EmptyState title="No messages yet" description="Sent and received messages will appear here." />
      ) : (
        <div className="flex flex-col gap-2">
          {logs.map((log) => (
            <div key={log._id} className="rounded-lg border border-outline bg-surface p-3">
              <div className="flex flex-wrap items-center gap-2">
                <Badge tone={log.channel === 'email' ? 'info' : 'success'}>{log.channel}</Badge>
                <Badge tone={log.status === 'sent' ? 'success' : log.status === 'received' ? 'info' : 'danger'}>
                  {log.status}
                </Badge>
                <span className="text-body-sm text-on-surface-variant">
                  {log.direction === 'out' ? `To ${log.to}` : `From ${log.from}`} ·{' '}
                  {new Date(log.createdAt).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' })}
                </span>
              </div>
              {log.subject && <p className="mt-1 font-medium text-on-surface">{log.subject}</p>}
              <p className="mt-1 line-clamp-3 whitespace-pre-wrap text-body-sm text-on-surface-variant">{log.body}</p>
              {log.error && <p className="mt-1 text-body-sm text-danger-text">{log.error}</p>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ── Email Templates tab ─────────────────────────────────────────────────── */

// Template scenario options grouped by department/category for new template creation.
// The templateKey is what the backend uses to look up templates at trigger points.
const TEMPLATE_SCENARIOS: Record<string, { label: string; key: string; description: string; variables: string[] }[]> = {
  hr: [
    { label: 'Offer Letter Sent', key: 'offer_letter_sent', description: 'Sent to candidate when their offer letter is dispatched', variables: ['candidateName', 'candidateEmail', 'designation', 'department', 'joiningDate', 'workLocation', 'companyName'] },
    { label: 'Interview Scheduled', key: 'interview_scheduled', description: 'Sent to candidate when their interview is scheduled', variables: ['candidateName', 'candidateEmail', 'jobTitle', 'interviewDate', 'interviewTime', 'location', 'companyName'] },
    { label: 'Offer Accepted — E-Joining Link', key: 'offer_accepted_ejoining', description: 'Sent to candidate after they accept the offer, containing the E-Joining portal link', variables: ['candidateName', 'candidateEmail', 'designation', 'department', 'joiningDate', 'eJoiningLink', 'companyName'] },
    { label: 'Candidate Rejected', key: 'candidate_rejected', description: 'Sent to candidate when their application is rejected', variables: ['candidateName', 'candidateEmail', 'jobTitle', 'companyName'] },
    { label: 'Welcome / Onboarding', key: 'welcome_onboarding', description: 'Welcome email sent on joining day with first-day information', variables: ['candidateName', 'designation', 'department', 'joiningDate', 'reportingTo', 'companyName'] },
    { label: 'Probation Confirmation', key: 'probation_confirmed', description: 'Sent to employee when probation is successfully completed', variables: ['candidateName', 'designation', 'department', 'confirmationDate', 'companyName'] },
  ],
  crm: [
    { label: 'Quotation Sent', key: 'quotation_sent', description: 'Sent to client when a quotation is shared', variables: ['clientName', 'clientEmail', 'quotationNumber', 'totalAmount', 'currency', 'validUntil', 'companyName'] },
    { label: 'Proforma Invoice Sent', key: 'proforma_invoice_sent', description: 'Sent to client when a proforma invoice is shared', variables: ['clientName', 'clientEmail', 'invoiceNumber', 'totalAmount', 'currency', 'dueDate', 'companyName'] },
    { label: 'Invoice Sent', key: 'invoice_sent', description: 'Sent to client when a final invoice is shared', variables: ['clientName', 'clientEmail', 'invoiceNumber', 'totalAmount', 'currency', 'dueDate', 'companyName'] },
    { label: 'Follow-Up Reminder', key: 'follow_up_reminder', description: 'Follow-up email sent to leads/contacts after initial contact', variables: ['contactName', 'contactEmail', 'companyName', 'lastContactDate'] },
    { label: 'Deal Won Confirmation', key: 'deal_won_confirmation', description: 'Sent to client when a deal is closed-won', variables: ['clientName', 'clientEmail', 'dealTitle', 'dealValue', 'currency', 'companyName'] },
  ],
};

const CATEGORY_OPTIONS = [
  { label: 'HR', value: 'hr' },
  { label: 'CRM', value: 'crm' },
];

function TemplatesTab() {
  const { data: templates = [], isLoading } = useEmailTemplates();
  const updateTemplate = useUpdateEmailTemplate();
  const toggleTemplate = useToggleEmailTemplate();
  const previewTemplate = usePreviewEmailTemplate();

  const [editingTemplate, setEditingTemplate] = useState<typeof templates[0] | null>(null);
  const [editSubject, setEditSubject] = useState('');
  const [editBody, setEditBody] = useState('');
  const [previewHtml, setPreviewHtml] = useState<string | null>(null);
  const [previewSubject, setPreviewSubject] = useState('');

  // Create new template state
  const [showCreate, setShowCreate] = useState(false);
  const [createCategory, setCreateCategory] = useState<'hr' | 'crm'>('hr');
  const [createScenarioKey, setCreateScenarioKey] = useState('');
  const [createSubject, setCreateSubject] = useState('');
  const [createBody, setCreateBody] = useState('');

  const availableScenarios = TEMPLATE_SCENARIOS[createCategory] || [];
  const selectedScenario = availableScenarios.find(s => s.key === createScenarioKey);
  // Filter out scenarios that already have a template
  const existingKeys = new Set(templates.map(t => t.templateKey));
  const creatableScenarios = availableScenarios.filter(s => !existingKeys.has(s.key));

  const handleOpenCreate = () => {
    setShowCreate(true);
    setCreateCategory('hr');
    setCreateScenarioKey('');
    setCreateSubject('');
    setCreateBody('');
  };

  const handleCreateTemplate = () => {
    if (!createScenarioKey || !createSubject.trim() || !createBody.trim()) {
      toast.error('Select a scenario and fill in the subject and body.');
      return;
    }
    const scenario = selectedScenario;
    if (!scenario) return;
    updateTemplate.mutate(
      {
        key: scenario.key,
        subject: createSubject.trim(),
        bodyHtml: createBody.trim(),
        name: scenario.label,
        description: scenario.description,
        category: createCategory,
        variables: scenario.variables,
      },
      {
        onSuccess: () => { toast.success('Template created.'); setShowCreate(false); },
        onError: (err) => toast.error(errorMessage(err, 'Failed to create template.')),
      },
    );
  };

  const handleEdit = (t: typeof templates[0]) => {
    setEditingTemplate(t);
    setEditSubject(t.subject);
    setEditBody(t.bodyHtml);
  };

  const handleSave = () => {
    if (!editingTemplate) return;
    updateTemplate.mutate(
      {
        key: editingTemplate.templateKey,
        subject: editSubject,
        bodyHtml: editBody,
        name: editingTemplate.name,
        description: editingTemplate.description,
        category: editingTemplate.category,
        variables: editingTemplate.variables,
      },
      {
        onSuccess: () => { toast.success('Template saved.'); setEditingTemplate(null); },
        onError: (err) => toast.error(errorMessage(err, 'Failed to save.')),
      },
    );
  };

  const handlePreview = (t: typeof templates[0]) => {
    previewTemplate.mutate(
      { key: t.templateKey },
      {
        onSuccess: (result) => { setPreviewSubject(result.subject); setPreviewHtml(result.html); },
        onError: (err) => toast.error(errorMessage(err, 'Preview failed.')),
      },
    );
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-body-lg font-semibold text-on-surface">Email Templates</h2>
          <p className="text-body-sm text-on-surface-variant">
            Manage email templates used across HR and CRM workflows. Templates use Handlebars syntax for dynamic variables.
          </p>
        </div>
        <Button size="sm" onClick={handleOpenCreate}>
          <Plus className="h-4 w-4 mr-1" />
          Create Template
        </Button>
      </div>

      {isLoading ? (
        <div className="flex h-24 items-center justify-center"><Spinner /></div>
      ) : templates.length === 0 ? (
        <EmptyState title="No templates" description="Templates will be auto-created on first load." />
      ) : (
        <Card>
          <CardBody className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-sm text-left">
                <thead className="text-xs uppercase bg-surface-variant/50 border-b border-outline">
                  <tr>
                    <th className="px-4 py-3">Template</th>
                    <th className="px-4 py-3">Category</th>
                    <th className="px-4 py-3">Description</th>
                    <th className="px-4 py-3 text-center">Status</th>
                    <th className="px-4 py-3">Last Updated</th>
                    <th className="px-4 py-3 text-center">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-outline">
                  {templates.map((t) => (
                    <tr key={t.templateKey} className="hover:bg-surface-variant/30">
                      <td className="px-4 py-3 font-medium text-on-surface">{t.name}</td>
                      <td className="px-4 py-3">
                        <Badge tone={t.category === 'hr' ? 'info' : 'success'}>{t.category.toUpperCase()}</Badge>
                      </td>
                      <td className="px-4 py-3 text-on-surface-variant text-xs max-w-[200px] truncate">{t.description}</td>
                      <td className="px-4 py-3 text-center">
                        <button
                          type="button"
                          onClick={() => toggleTemplate.mutate(t.templateKey)}
                          className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${t.active ? 'bg-success' : 'bg-gray-300'}`}
                          title={t.active ? 'Active — click to deactivate' : 'Inactive — click to activate'}
                        >
                          <span className={`inline-block h-3.5 w-3.5 rounded-full bg-white transition-transform ${t.active ? 'translate-x-4.5' : 'translate-x-0.5'}`} />
                        </button>
                      </td>
                      <td className="px-4 py-3 text-xs text-on-surface-variant">
                        {t.updatedAt ? new Date(t.updatedAt).toLocaleDateString('en-IN', { dateStyle: 'medium' }) : '—'}
                      </td>
                      <td className="px-4 py-3 text-center">
                        <div className="flex items-center justify-center gap-1">
                          <Button variant="ghost" size="sm" onClick={() => handleEdit(t)} title="Edit">
                            <Pencil className="h-3.5 w-3.5" />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => handlePreview(t)} title="Preview" disabled={previewTemplate.isPending}>
                            <Eye className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardBody>
        </Card>
      )}

      {/* Edit Template Modal */}
      <Modal open={!!editingTemplate} onClose={() => setEditingTemplate(null)} title={`Edit: ${editingTemplate?.name || ''}`} size="lg" footer={
        <div className="flex gap-2">
          <Button variant="ghost" onClick={() => setEditingTemplate(null)}>Cancel</Button>
          <Button onClick={handleSave} disabled={updateTemplate.isPending}>{updateTemplate.isPending ? 'Saving...' : 'Save Template'}</Button>
        </div>
      }>
        {editingTemplate && (
          <div className="space-y-4 max-h-[60vh] overflow-auto">
            <div className="rounded-md bg-primary/5 border border-primary/20 p-3">
              <p className="text-body-sm font-medium text-on-surface">Available variables:</p>
              <div className="mt-1 flex flex-wrap gap-1">
                {editingTemplate.variables.map((v) => (
                  <code key={v} className="rounded bg-surface-variant px-1.5 py-0.5 text-xs font-mono text-primary">{`{{${v}}}`}</code>
                ))}
              </div>
            </div>
            <div>
              <label className="mb-1.5 block text-body-sm font-medium text-on-surface">Subject line</label>
              <input
                type="text"
                className="w-full rounded border border-outline bg-surface px-3 py-2 text-data-md text-on-surface focus:outline-none focus:ring-2 focus:ring-primary/40"
                value={editSubject}
                onChange={(e) => setEditSubject(e.target.value)}
              />
            </div>
            <div>
              <label className="mb-1.5 block text-body-sm font-medium text-on-surface">Body HTML (Handlebars)</label>
              <textarea
                rows={14}
                className="w-full rounded border border-outline bg-surface px-3 py-2 text-xs font-mono text-on-surface focus:outline-none focus:ring-2 focus:ring-primary/40"
                value={editBody}
                onChange={(e) => setEditBody(e.target.value)}
              />
            </div>
          </div>
        )}
      </Modal>

      {/* Preview Modal */}
      <Modal open={!!previewHtml} onClose={() => setPreviewHtml(null)} title={`Preview: ${previewSubject}`} size="lg" footer={
        <Button onClick={() => setPreviewHtml(null)}>Close</Button>
      }>
        {previewHtml && (
          <iframe
            srcDoc={previewHtml}
            title="Email Preview"
            className="w-full h-[500px] border border-outline rounded-md"
            sandbox="allow-same-origin"
          />
        )}
      </Modal>

      {/* Create Template Modal */}
      <Modal open={showCreate} onClose={() => setShowCreate(false)} title="Create Email Template" size="lg" footer={
        <div className="flex gap-2">
          <Button variant="ghost" onClick={() => setShowCreate(false)}>Cancel</Button>
          <Button onClick={handleCreateTemplate} disabled={updateTemplate.isPending || !createScenarioKey}>
            {updateTemplate.isPending ? 'Creating...' : 'Create Template'}
          </Button>
        </div>
      }>
        <div className="space-y-4 max-h-[60vh] overflow-auto py-2">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="mb-1.5 block text-body-sm font-medium text-on-surface">Department</label>
              <select
                className="w-full rounded border border-outline bg-surface px-3 py-2 text-data-md text-on-surface focus:outline-none focus:ring-2 focus:ring-primary/40"
                value={createCategory}
                onChange={(e) => { setCreateCategory(e.target.value as 'hr' | 'crm'); setCreateScenarioKey(''); }}
              >
                {CATEGORY_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </div>
            <div>
              <label className="mb-1.5 block text-body-sm font-medium text-on-surface">Scenario</label>
              <select
                className="w-full rounded border border-outline bg-surface px-3 py-2 text-data-md text-on-surface focus:outline-none focus:ring-2 focus:ring-primary/40"
                value={createScenarioKey}
                onChange={(e) => {
                  setCreateScenarioKey(e.target.value);
                  const scenario = availableScenarios.find(s => s.key === e.target.value);
                  if (scenario) {
                    setCreateSubject(`{{companyName}} — ${scenario.label}`);
                    setCreateBody(`<p>Dear <strong>{{${scenario.variables[0] || 'name'}}}</strong>,</p>\n<p><!-- Your content here --></p>\n<p>Best regards,<br><strong>{{companyName}}</strong></p>`);
                  }
                }}
              >
                <option value="">— Select scenario —</option>
                {creatableScenarios.map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
                {creatableScenarios.length === 0 && <option disabled>All scenarios already have templates</option>}
              </select>
            </div>
          </div>

          {selectedScenario && (
            <>
              <div className="rounded-md bg-primary/5 border border-primary/20 p-3">
                <p className="text-body-sm text-on-surface-variant">{selectedScenario.description}</p>
                <p className="text-body-sm font-medium text-on-surface mt-2">Available variables:</p>
                <div className="mt-1 flex flex-wrap gap-1">
                  {selectedScenario.variables.map((v) => (
                    <code key={v} className="rounded bg-surface-variant px-1.5 py-0.5 text-xs font-mono text-primary">{`{{${v}}}`}</code>
                  ))}
                </div>
              </div>
              <div>
                <label className="mb-1.5 block text-body-sm font-medium text-on-surface">Subject line</label>
                <input
                  type="text"
                  className="w-full rounded border border-outline bg-surface px-3 py-2 text-data-md text-on-surface focus:outline-none focus:ring-2 focus:ring-primary/40"
                  value={createSubject}
                  onChange={(e) => setCreateSubject(e.target.value)}
                  placeholder="e.g. Interview Scheduled — {{jobTitle}} at {{companyName}}"
                />
              </div>
              <div>
                <label className="mb-1.5 block text-body-sm font-medium text-on-surface">Body HTML (Handlebars)</label>
                <textarea
                  rows={12}
                  className="w-full rounded border border-outline bg-surface px-3 py-2 text-xs font-mono text-on-surface focus:outline-none focus:ring-2 focus:ring-primary/40"
                  value={createBody}
                  onChange={(e) => setCreateBody(e.target.value)}
                  placeholder="<p>Dear {{candidateName}},</p>&#10;<p>...</p>"
                />
              </div>
            </>
          )}
        </div>
      </Modal>
    </div>
  );
}
